import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Users2, Plus, Pencil, Mail, Trash2, X, Check, Loader2, UserCircle2, Camera, Eye, Bell, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { managedPlayersApi } from '../services/managedPlayersApi';
import profileImageService from '../services/profileImageService';
import ImageCropModal from '../../components/common/ImageCropModal';
import { moveImage } from '../../services/imageUploadService';
import { btn } from '../../utils/buttonStyles';
import { resolveImageUrl } from '../hooks/useProfileImage';
import { apiCall } from '../../services/apiUtil';
import { useImagePicker } from '../../hooks/useImagePicker.jsx';

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Prefer not to say'];
const T_SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

const emptyForm = {
  firstName: '',
  lastName: '',
  email: '',
  dob: '',
  gender: '',
  tShirtSize: ''
};

// Defined outside ManagedPlayersTab so React never remounts it on re-renders
function PlayerForm({ form, onChange, onSubmit, onCancel, submitLabel, saving, inputClass, labelClass, theme, onImageFile, currentImageUrl }) {
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedForCrop, setSelectedForCrop] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(currentImageUrl || null);

  // Sync preview when currentImageUrl loads in asynchronously (edit mode)
  useEffect(() => {
    if (currentImageUrl && !previewUrl) {
      setPreviewUrl(currentImageUrl);
    }
  }, [currentImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  const { openPicker, pickerInput } = useImagePicker({
    accept: 'image/*',
    onFiles: (files) => {
      const file = files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        setSelectedForCrop(reader.result)
        setCropModalOpen(true)
      }
      reader.readAsDataURL(file)
    },
  });

  // Called only on moderation success, with the upload result
  const handleCropComplete = ({ url }) => {
    setPreviewUrl(resolveImageUrl(url));
    onImageFile(url);
  };

  return (
    <>
      <form onSubmit={onSubmit} className="space-y-3">
        {/* Profile image picker */}
        <div className="flex justify-center mb-1">
          <div className="relative">
            <button
              type="button"
              onClick={openPicker}
              className="w-20 h-20 rounded-full overflow-hidden border-2 border-dashed border-blue-400 hover:border-blue-600 transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500"
              title="Upload profile photo"
            >
              {previewUrl ? (
                <img src={previewUrl} alt="Preview" className="w-full h-full object-cover" />
              ) : (
                <UserCircle2 className={`w-12 h-12 ${theme.textMuted}`} />
              )}
            </button>
            <div className="absolute bottom-0 right-0 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center pointer-events-none">
              <Camera className="w-3.5 h-3.5 text-white" />
            </div>
            {pickerInput}
          </div>
        </div>
        <p className={`text-center text-xs ${theme.textMuted} -mt-1 mb-2`}>Click photo to change</p>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>First Name *</label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => onChange({ ...form, firstName: e.target.value })}
              className={inputClass}
              placeholder="First name"
              required
            />
          </div>
          <div>
            <label className={labelClass}>Last Name *</label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => onChange({ ...form, lastName: e.target.value })}
              className={inputClass}
              placeholder="Last name"
              required
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>Email (for invite)</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => onChange({ ...form, email: e.target.value })}
            className={inputClass}
            placeholder="player@example.com"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={labelClass}>Date of Birth</label>
            <input
              type="date"
              value={form.dob}
              onChange={(e) => onChange({ ...form, dob: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Gender</label>
            <select
              value={form.gender}
              onChange={(e) => onChange({ ...form, gender: e.target.value })}
              className={inputClass}
            >
              <option value="">Select</option>
              {GENDER_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>T-Shirt Size</label>
            <select
              value={form.tShirtSize}
              onChange={(e) => onChange({ ...form, tShirtSize: e.target.value })}
              className={inputClass}
            >
              <option value="">Select</option>
              {T_SHIRT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button
            type="submit"
            disabled={saving}
            className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-1.5 disabled:opacity-50`}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {submitLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className={`flex items-center gap-1.5 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
          >
            <X className="w-4 h-4" />
            Cancel
          </button>
        </div>
      </form>

      <ImageCropModal
        isOpen={cropModalOpen}
        onClose={() => setCropModalOpen(false)}
        imageSrc={selectedForCrop}
        fileType="profile_images"
        onCropComplete={handleCropComplete}
        shape="round"
        aspectRatio={1}
      />
    </>
  );
}

export default function ManagedPlayersTab() {
  const { theme } = useTheme();
  const { user } = useAuth();

  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editImageUrl, setEditImageUrl] = useState(null);
  const [editCurrentImageUrl, setEditCurrentImageUrl] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm);
  const [createImageUrl, setCreateImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [inviting, setInviting] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [confirmInvite, setConfirmInvite] = useState(null); // { type: 'create'|'edit', id, email, inviterName }
  const [pendingInvitations, setPendingInvitations] = useState({}); // keyed by playerId
  const [invitationActionLoading, setInvitationActionLoading] = useState(null); // `${playerId}-${groupId}`
  const [refreshing, setRefreshing] = useState(false);

  const inputClass = `w-full px-3 py-2 rounded-lg border ${theme.border} ${theme.background} ${theme.text} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm`;
  const labelClass = `block text-xs font-medium mb-1 ${theme.textMuted}`;

  const loadPlayers = useCallback(async () => {
    try {
      setLoading(true);
      const result = await managedPlayersApi.getAll();
      setPlayers(result.data || []);
    } catch (err) {
      toast.error('Failed to load managed players');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const loadPendingInvitations = useCallback(async () => {
    try {
      const r = await apiCall('/invitations/managed-player/pending');
      setPendingInvitations(r.invitations || {});
    } catch {
      setPendingInvitations({});
    }
  }, []);

  useEffect(() => {
    loadPendingInvitations();
  }, [loadPendingInvitations]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadPlayers(), loadPendingInvitations()]);
    setRefreshing(false);
  };

  const handleAcceptInvitation = async (playerId, groupId) => {
    const key = `${playerId}-${groupId}`;
    setInvitationActionLoading(key);
    try {
      await apiCall(`/invitations/managed-player/${playerId}/accept`, { method: 'POST', body: JSON.stringify({ groupId }) });
      toast.success('Invitation accepted');
      setPendingInvitations(prev => ({
        ...prev,
        [playerId]: (prev[playerId] || []).filter(i => i.groupId !== groupId)
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to accept invitation');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const handleDeclineInvitation = async (playerId, groupId) => {
    const key = `${playerId}-${groupId}`;
    setInvitationActionLoading(key);
    try {
      await apiCall(`/invitations/managed-player/${playerId}/decline`, { method: 'POST', body: JSON.stringify({ groupId }) });
      toast.success('Invitation declined');
      setPendingInvitations(prev => ({
        ...prev,
        [playerId]: (prev[playerId] || []).filter(i => i.groupId !== groupId)
      }));
    } catch (err) {
      toast.error(err.message || 'Failed to decline invitation');
    } finally {
      setInvitationActionLoading(null);
    }
  };

  const inviterName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';

  // Actually perform save + invite after confirmation modal OK
  const handleConfirmInvite = async () => {
    const { type, id, email } = confirmInvite;
    setConfirmInvite(null);
    setSaving(true);
    try {
      if (type === 'create') {
        const newPlayer = await managedPlayersApi.create(createForm);
        if (createImageUrl && newPlayer?.id) {
          try {
            const { url: canonicalUrl } = await moveImage(createImageUrl, `profile_images/${newPlayer.id}/${crypto.randomUUID()}.jpeg`);
            await apiCall(`/user/${newPlayer.id}`, { method: 'PATCH', body: JSON.stringify({ profileImage: canonicalUrl }) });
          } catch (imgErr) {
            toast.error('Player created but profile image save failed.');
          }
        }
        toast.success('Player added and added to your teammates');
        setCreateForm(emptyForm);
        setCreateImageUrl(null);
        setShowCreateForm(false);
        await loadPlayers();
        try { await managedPlayersApi.invite(newPlayer.id, inviterName); } catch {}
      } else {
        if (editImageUrl) {
          try {
            const { url: canonicalUrl } = await moveImage(editImageUrl, `profile_images/${id}/${crypto.randomUUID()}.jpeg`);
            await apiCall(`/user/${id}`, { method: 'PATCH', body: JSON.stringify({ profileImage: canonicalUrl }) });
          } catch (imgErr) {
            toast.error('Player updated but profile image save failed.');
          }
        }
        await managedPlayersApi.update(id, editForm);
        toast.success('Player updated');
        setEditingId(null);
        setEditImageUrl(null);
        setEditCurrentImageUrl(null);
        await loadPlayers();
        try { await managedPlayersApi.invite(id, inviterName); } catch {}
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save player');
    } finally {
      setSaving(false);
    }
  };

  // Cancel invite confirmation — for create: dismiss modal only; for edit: save without invite
  const handleCancelInvite = () => {
    const { type, id } = confirmInvite;
    setConfirmInvite(null);
    if (type === 'edit') {
      doSaveEdit(id);
    }
    // For create: user stays in the form with their data intact
  };

  const validateDob = (dob) => {
    if (!dob) return true;
    const dobDate = new Date(dob);
    const today = new Date();
    const age = today.getFullYear() - dobDate.getFullYear() -
      ((today.getMonth() < dobDate.getMonth() ||
        (today.getMonth() === dobDate.getMonth() && today.getDate() < dobDate.getDate())) ? 1 : 0);
    return age >= 13;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!createForm.firstName || !createForm.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    if (!validateDob(createForm.dob)) {
      toast.error('Player must be at least 13 years old.');
      return;
    }
    // If email is set, show confirmation modal first
    if (createForm.email) {
      setConfirmInvite({ type: 'create', email: createForm.email });
      return;
    }
    // No email — save directly
    setSaving(true);
    try {
      const newPlayer = await managedPlayersApi.create(createForm);
      if (createImageUrl && newPlayer?.id) {
        try {
          const { url: canonicalUrl } = await moveImage(createImageUrl, `profile_images/${newPlayer.id}/${crypto.randomUUID()}.jpeg`);
          await apiCall(`/user/${newPlayer.id}`, { method: 'PATCH', body: JSON.stringify({ profileImage: canonicalUrl }) });
        } catch (imgErr) {
          toast.error('Player created but profile image save failed.');
        }
      }
      toast.success('Player added and added to your teammates');
      setCreateForm(emptyForm);
      setCreateImageUrl(null);
      setShowCreateForm(false);
      await loadPlayers();
    } catch (err) {
      toast.error(err.message || 'Failed to add player');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async (player) => {
    setEditingId(player.id);
    setEditImageUrl(null);
    setEditCurrentImageUrl(null);
    setEditForm({
      firstName: player.firstName || '',
      lastName: player.lastName || '',
      email: player.email || '',
      dob: player.dob ? player.dob.split('T')[0] : '',
      gender: player.gender || '',
      tShirtSize: player.tShirtSize || ''
    });
    // Load current image asynchronously
    if (player.profileImage) {
      profileImageService.getProfileImageUrl(player.id)
        .then(url => { if (url) setEditCurrentImageUrl(url); })
        .catch(() => {});
    }
  };

  const handleSaveEdit = (id) => {
    if (!validateDob(editForm.dob)) {
      toast.error('Player must be at least 13 years old.');
      return;
    }
    const previousEmail = players.find(p => p.id === id)?.email || '';
    const newEmail = editForm.email;
    const emailAdded = newEmail && newEmail !== previousEmail;
    // If email newly added/changed, show confirmation modal first
    if (emailAdded) {
      setConfirmInvite({ type: 'edit', id, email: newEmail });
      return;
    }
    // No email change — save directly
    doSaveEdit(id);
  };

  const doSaveEdit = async (id) => {
    setSaving(true);
    try {
      if (editImageUrl) {
        try {
          const { url: canonicalUrl } = await moveImage(editImageUrl, `profile_images/${id}/${crypto.randomUUID()}.jpeg`);
          await apiCall(`/user/${id}`, { method: 'PATCH', body: JSON.stringify({ profileImage: canonicalUrl }) });
        } catch (imgErr) {
          toast.error('Player updated but profile image save failed.');
        }
      }
      await managedPlayersApi.update(id, editForm);
      toast.success('Player updated');
      setEditingId(null);
      setEditImageUrl(null);
      setEditCurrentImageUrl(null);
      await loadPlayers();
    } catch (err) {
      toast.error(err.message || 'Failed to update player');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await managedPlayersApi.delete(id);
      toast.success('Player removed');
      setConfirmDeleteId(null);
      await loadPlayers();
    } catch (err) {
      toast.error(err.message || 'Failed to delete player');
    } finally {
      setDeleting(null);
    }
  };

  const handleInvite = async (player) => {
    setInviting(player.id);
    try {
      const inviterName = user ? `${user.firstName || ''} ${user.lastName || ''}`.trim() : '';
      await managedPlayersApi.invite(player.id, inviterName);
      toast.success(`Invite sent to ${player.email}`);
    } catch (err) {
      toast.error(err.message || 'Failed to send invite');
    } finally {
      setInviting(null);
    }
  };

  const sharedFormProps = { saving, inputClass, labelClass, theme };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Users2 className="w-6 h-6 text-blue-600 mr-3" />
          <div>
            <h2 className={`text-xl font-semibold ${theme.text}`}>Managed Players</h2>
            <p className={`text-sm ${theme.textMuted}`}>Players you manage for scoring and team participation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Refresh"
            className={`p-2 rounded-lg ${theme.backgroundSecondary} ${theme.text} hover:opacity-80 disabled:opacity-40`}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          {!showCreateForm && (
            <button
              onClick={() => setShowCreateForm(true)}
              className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex items-center gap-2`}
            >
              <Plus className="w-4 h-4" />
              Add Player
            </button>
          )}
        </div>
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className={`mb-6 p-4 rounded-xl border ${theme.border} ${theme.backgroundSecondary}`}>
          <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>New Player</h3>
          <PlayerForm
            form={createForm}
            onChange={setCreateForm}
            onSubmit={handleCreate}
            onCancel={() => { setShowCreateForm(false); setCreateForm(emptyForm); setCreateImageUrl(null); }}
            submitLabel="Add Player"
            onImageFile={setCreateImageUrl}
            currentImageUrl={null}
            {...sharedFormProps}
          />
        </div>
      )}

      {/* Player list */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className={`w-8 h-8 animate-spin ${theme.textMuted}`} />
        </div>
      ) : players.length === 0 ? (
        <div className={`text-center py-12 ${theme.textMuted}`}>
          <Users2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No managed players yet</p>
          <p className="text-sm mt-1">Add players to manage their participation in games and tournaments</p>
        </div>
      ) : (
        <div className="space-y-3">
          {players.map(player => (
            <div key={player.id} className={`rounded-xl border ${theme.border} ${theme.card} overflow-hidden`}>
              {editingId === player.id ? (
                <div className="p-4">
                  <h3 className={`text-sm font-semibold mb-3 ${theme.text}`}>
                    Editing {player.firstName} {player.lastName}
                  </h3>
                  <PlayerForm
                    form={editForm}
                    onChange={setEditForm}
                    onSubmit={(e) => { e.preventDefault(); handleSaveEdit(player.id); }}
                    onCancel={() => { setEditingId(null); setEditImageUrl(null); setEditCurrentImageUrl(null); }}
                    submitLabel="Save Changes"
                    onImageFile={setEditImageUrl}
                    currentImageUrl={editCurrentImageUrl}
                    {...sharedFormProps}
                  />
                </div>
              ) : confirmDeleteId === player.id ? (
                <div className="p-4">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${theme.text} mb-1`}>
                        Permanently delete {player.firstName} {player.lastName}?
                      </p>
                      <p className={`text-xs ${theme.textMuted} mb-2`}>
                        This action <strong>cannot be undone</strong>. The following will be permanently deleted:
                      </p>
                      <ul className={`text-xs ${theme.textMuted} space-y-0.5 list-disc list-inside`}>
                        <li>All personal data for this player (name, DOB, contact info, profile photo)</li>
                        <li>Their membership in any teams they were added to</li>
                        <li>Their teammate connection with you</li>
                        <li>Any game scores or statistics recorded under this player ID</li>
                      </ul>
                      <p className={`text-xs text-red-500 font-medium mt-2`}>
                        If this player has already claimed their account, deleting it here will lock them out permanently.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(player.id)}
                      disabled={deleting === player.id}
                      className={`flex items-center gap-1.5 ${btn.base} ${btn.sm} ${theme.buttonDanger}`}
                    >
                      {deleting === player.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Yes, permanently delete
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      className={`${btn.base} ${btn.sm} ${theme.buttonGhost}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <PlayerCard
                  player={player}
                  theme={theme}
                  onEdit={handleEdit}
                  onInvite={handleInvite}
                  onDelete={setConfirmDeleteId}
                  inviting={inviting}
                  pendingInvitations={pendingInvitations[player.id] || []}
                  onAcceptInvitation={handleAcceptInvitation}
                  onDeclineInvitation={handleDeclineInvitation}
                  invitationActionLoading={invitationActionLoading}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Invite confirmation modal */}
      {confirmInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className={`w-full max-w-sm rounded-2xl ${theme.card} shadow-xl p-6`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className={`text-base font-semibold ${theme.text}`}>Send Invite?</h3>
            </div>
            <p className={`text-sm ${theme.textSecondary} mb-1`}>
              An invite email will be sent to:
            </p>
            <p className="text-sm font-medium text-blue-600 mb-4 break-all">{confirmInvite.email}</p>
            <p className={`text-xs ${theme.textMuted} mb-5`}>
              This will allow the player to claim their account and set their own password. Once claimed, they will be removed as a managed player from your profile.
              {confirmInvite.type === 'edit' && ' The player data will be saved regardless.'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleConfirmInvite}
                disabled={saving}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 flex items-center justify-center gap-2 disabled:opacity-50`}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                OK, send invite
              </button>
              <button
                onClick={handleCancelInvite}
                disabled={saving}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
              >
                {confirmInvite.type === 'edit' ? 'Save without invite' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Separate component so each card can independently load its image
function PlayerCard({ player, theme, onEdit, onInvite, onDelete, inviting, pendingInvitations = [], onAcceptInvitation, onDeclineInvitation, invitationActionLoading }) {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    if (player.profileImage) {
      profileImageService.getProfileImageUrl(player.id)
        .then(url => { if (url) setImageUrl(url); })
        .catch(() => {});
    }
  }, [player.id, player.profileImage]);

  return (
    <div>
      <div className="p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${theme.backgroundSecondary}`}>
        {imageUrl ? (
          <img src={imageUrl} alt={player.firstName} className="w-full h-full object-cover" />
        ) : (
          <UserCircle2 className={`w-6 h-6 ${theme.textMuted}`} />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => navigate(`/player/${player.userHandle || player.id}`)}
            className={`font-semibold ${theme.text} hover:underline text-left`}
          >
            {player.firstName} {player.lastName}
          </button>
          {player.email ? (
            <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
              Email set
            </span>
          ) : (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
              No email
            </span>
          )}
        </div>
        <div className={`flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs ${theme.textMuted}`}>
          {player.dob && <span>DOB: {new Date(player.dob).toLocaleDateString()}</span>}
          {player.gender && <span>{player.gender}</span>}
          {player.tShirtSize && <span>Size: {player.tShirtSize}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => navigate(`/player/${player.userHandle || player.id}`)}
          title="View profile"
          className={`p-2 rounded-lg ${theme.backgroundSecondary} ${theme.text} hover:opacity-80`}
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          onClick={() => onEdit(player)}
          title="Edit player"
          className={`p-2 rounded-lg ${theme.backgroundSecondary} ${theme.text} hover:opacity-80`}
        >
          <Pencil className="w-4 h-4" />
        </button>
        {player.email && (
          <button
            onClick={() => onInvite(player)}
            disabled={inviting === player.id}
            title="Send invite"
            className="p-2 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:opacity-50"
          >
            {inviting === player.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
          </button>
        )}
        <button
          onClick={() => onDelete(player.id)}
          title="Remove player"
          className="p-2 rounded-lg bg-red-50 text-red-500 hover:bg-red-100"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
      </div>

    {pendingInvitations.length > 0 && (
      <div className={`px-4 pb-3 border-t ${theme.border}`}>
        <div className={`flex items-center gap-1.5 mt-2 mb-2 text-xs font-medium ${theme.textMuted}`}>
          <Bell className="w-3.5 h-3.5" />
          Pending Invitations ({pendingInvitations.length})
        </div>
        <div className="space-y-1.5">
          {pendingInvitations.map(inv => {
            const key = `${player.id}-${inv.groupId}`;
            const loading = invitationActionLoading === key;
            return (
              <div key={inv.groupId} className={`flex items-center justify-between gap-2 px-3 py-2 rounded-lg ${theme.backgroundSecondary}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${theme.text} truncate`}>{inv.groupName}</p>
                  <p className={`text-xs ${theme.textMuted}`}>{inv.sport || 'Squad'}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => onAcceptInvitation(player.id, inv.groupId)}
                    disabled={loading}
                    title="Accept"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 text-xs font-medium disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Accept
                  </button>
                  <button
                    onClick={() => onDeclineInvitation(player.id, inv.groupId)}
                    disabled={loading}
                    title="Decline"
                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 text-xs font-medium disabled:opacity-50"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                    Decline
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
  );
}

